import { Button, Card, Col, Form, Input, message, Modal, Row, Select, Breadcrumb, Divider, Tabs, Switch } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ExclamationCircleOutlined, ArrowLeftOutlined, SaveOutlined, DeleteOutlined,
    HomeOutlined, InfoCircleOutlined, PictureOutlined, SettingOutlined,
    SoundOutlined, VideoCameraOutlined, FileTextOutlined, BookOutlined
} from "@ant-design/icons";
import CustomUpload from "../customUpload";
import TextArea from "antd/es/input/TextArea";
import slugify from "slugify";
import convertVttToLrc from "../../utils/convertVttToLrc";
import convertSrtToLrc from "../../utils/convertSrtToLrc";

function TrackSection({ label, lrcName, audioName, videoName, required, handleVttChange }) {
    return (
        <div>
            <Form.Item
                label={<span className="font-semibold">{label} — Lời (LRC)</span>}
                name={lrcName}
                rules={required ? [{ required: true, message: "Vui lòng nhập nội dung!" }] : []}
            >
                <TextArea
                    placeholder={`Nhập nội dung ${label} (hỗ trợ paste VTT/SRT → tự động chuyển LRC)`}
                    style={{ height: "300px", fontFamily: "monospace", fontSize: "13px" }}
                    onChange={handleVttChange(lrcName)}
                />
            </Form.Item>
            <Row gutter={16}>
                <Col span={12}>
                    <Card size="small" className="bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2 font-semibold flex items-center gap-1">
                            <SoundOutlined className="text-blue-500" /> Audio
                        </p>
                        <Form.Item name={audioName} className="mb-2"
                            rules={required ? [{ required: true, message: "Vui lòng chọn audio!" }] : []}>
                            <Input size="small" placeholder="URL audio" />
                        </Form.Item>
                        <Form.Item name={audioName} className="mb-0">
                            <CustomUpload type="file" accept=".mp3" />
                        </Form.Item>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card size="small" className="bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2 font-semibold flex items-center gap-1">
                            <VideoCameraOutlined className="text-red-500" /> Video
                        </p>
                        <Form.Item name={videoName} className="mb-2">
                            <Input size="small" placeholder="URL video" />
                        </Form.Item>
                        <Form.Item name={videoName} className="mb-0">
                            <CustomUpload type="file" accept=".mp4, .webm, .ogg, .mov, .m4v" />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default function LessonForm({
    id = "",
    initialValues = {},
    onSubmit = () => {},
}) {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [courses, setCourses] = useState([]);
    const [pagination] = useState({ page: 1, pageSize: 100 });

    const fetchCourses = async () => {
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_API_URL}/course/getAll`, { params: pagination }
            );
            setCourses(result?.data?.data);
        } catch (e) {
            message.error("Không thể tải danh sách course");
        }
    };

    const deleteLesson = async () => {
        await axios
            .delete(`${process.env.REACT_APP_API_URL}/lesson/delete/${id}`)
            .then(() => {
                message.success("Xoá bài học thành công");
                navigate("/admin/lesson");
            });
    };

    const handleLessonSlugChange = (e) => {
        form.setFieldValue("slug", slugify(e.target.value, { lower: true }));
    };

    useEffect(() => {
        fetchCourses();
        if (Object.keys(initialValues)?.length > 0) {
            // reset then populate translation fields if present
            form.resetFields();
            try {
                let t = initialValues?.translations || {};
                if (typeof t === 'string') {
                    try { t = JSON.parse(t); } catch (e) { /* keep as string */ }
                }

                // Normalized outputs for textareas
                let mainStoryText = '';
                let vocabularyText = '';
                let miniStoryText = '';
                let POVText = '';
                let commentText = '';

                if (Array.isArray(t)) {
                    // legacy: translations stored as plain array (likely for mainStory)
                    mainStoryText = t.join('\n');
                } else if (typeof t === 'object' && t !== null) {
                    const toPlain = (v) => {
                        if (!v) return '';
                        if (Array.isArray(v)) return v.join('\n');
                        if (typeof v === 'string') return v;
                        try { return JSON.stringify(v); } catch (e) { return String(v); }
                    };
                    mainStoryText = toPlain(t.mainStory);
                    vocabularyText = toPlain(t.vocabulary);
                    miniStoryText = toPlain(t.miniStory);
                    POVText = toPlain(t.POV);
                    commentText = toPlain(t.comment);
                } else if (typeof t === 'string') {
                    // single string -> put into mainStory textarea
                    mainStoryText = t;
                }

                form.setFieldsValue({
                    translations_mainStory: mainStoryText,
                    translations_vocabulary: vocabularyText,
                    translations_miniStory: miniStoryText,
                    translations_POV: POVText,
                    translations_comment: commentText,
                });
            } catch (e) { console.error('Parse translations', e); }
        }
    }, [form, initialValues]);

    const confirmDelete = () => {
        Modal.confirm({
            icon: <ExclamationCircleOutlined />,
            content: "Bạn có chắc chắn xoá bài học này?",
            okText: "Xác nhận",
            cancelText: "Huỷ",
            onOk: () => deleteLesson(),
        });
    };

    const handleVttChange = (fieldName) => (e) => {
        const value = e.target.value;
        if (value && value.includes('-->')) {
            // Try SRT format first (has comma in timestamp)
            if (value.match(/\d{2}:\d{2}:\d{2},\d{3}/)) {
                const converted = convertSrtToLrc(value);
                if (converted !== value) {
                    form.setFieldValue(fieldName, converted);
                    message.success('Đã tự động chuyển đổi SRT sang LRC!');
                    return;
                }
            }
            // Try VTT format (has dot in timestamp)
            const converted = convertVttToLrc(value);
            if (converted !== value) {
                form.setFieldValue(fieldName, converted);
                message.success('Đã tự động chuyển đổi VTT sang LRC!');
            }
        }
    };

    const stripLrcToPlain = (lrc) => {
        if (!lrc || typeof lrc !== 'string') return '';
        return lrc.split('\n')
            .map(line => line.replace(/^\s*\[\d{1,2}:\d{2}(?:[:.]\d+)?\]\s*/, '').trim())
            .filter(Boolean)
            .join('\n');
    };

    const handleTranslationVttChange = (fieldName) => (e) => {
        const value = e.target.value;
        if (value && value.includes('-->')) {
            // Try SRT first (has comma in timestamp)
            if (value.match(/\d{2}:\d{2}:\d{2},\d{3}/)) {
                const converted = convertSrtToLrc(value);
                const plain = stripLrcToPlain(converted);
                if (plain !== value) {
                    form.setFieldValue(fieldName, plain);
                    message.success('Đã tự động chuyển SRT sang bản dịch không timestamp!');
                    return;
                }
            }
            // Try VTT
            const converted = convertVttToLrc(value);
            const plain = stripLrcToPlain(converted);
            if (plain !== value) {
                form.setFieldValue(fieldName, plain);
                message.success('Đã tự động chuyển VTT sang bản dịch không timestamp!');
            }
        }
    };

    const trackTabs = [
        {
            key: "mainStory",
            label: <span className="flex items-center gap-1"><BookOutlined />Main Story</span>,
            children: <TrackSection label="Main Story" lrcName="mainStory" audioName="mainStoryAudio" videoName="mainStoryVideo" required handleVttChange={handleVttChange} />,
        },
        {
            key: "vocabulary",
            label: <span className="flex items-center gap-1"><FileTextOutlined />Vocabulary</span>,
            children: <TrackSection label="Vocabulary" lrcName="vocabulary" audioName="vocabularyAudio" videoName="vocabularyVideo" handleVttChange={handleVttChange} />,
        },
        {
            key: "miniStory",
            label: <span className="flex items-center gap-1"><BookOutlined />Mini Story</span>,
            children: <TrackSection label="Mini Story" lrcName="miniStory" audioName="miniStoryAudio" videoName="miniStoryVideo" handleVttChange={handleVttChange} />,
        },
        {
            key: "POV",
            label: <span className="flex items-center gap-1"><FileTextOutlined />POV</span>,
            children: <TrackSection label="POV" lrcName="POV" audioName="POVAudio" videoName="POVVideo" handleVttChange={handleVttChange} />,
        },
        {
            key: "comment",
            label: <span className="flex items-center gap-1"><FileTextOutlined />Comment</span>,
            children: <TrackSection label="Comment" lrcName="comment" audioName="commentAudio" videoName="commentVideo" handleVttChange={handleVttChange} />,
        },
    ];

    return (
        <div>
            {/* Header */}
            <div className="mb-6">
                <Breadcrumb items={[
                    { title: <Link to="/admin"><HomeOutlined /> Dashboard</Link> },
                    { title: <Link to="/admin/lesson">Lesson</Link> },
                    { title: id ? "Chỉnh sửa" : "Tạo mới" },
                ]} />
                <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-3">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/admin/lesson")} />
                        <h1 className="text-2xl font-bold text-gray-800 m-0">
                            {id ? "Chỉnh sửa Lesson" : "Tạo Lesson mới"}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {id && (
                            <Button danger icon={<DeleteOutlined />} size="large" onClick={confirmDelete}>
                                Xoá
                            </Button>
                        )}
                        <Button type="primary" size="large" icon={<SaveOutlined />}
                            className="bg-blue-500 min-w-[140px] font-semibold shadow-md"
                            onClick={() => form.submit()}>
                            {id ? "Cập nhật Lesson" : "Tạo Lesson"}
                        </Button>
                    </div>
                </div>
            </div>

            <Form
                layout="vertical"
                colon={false}
                form={form}
                initialValues={initialValues}
                onFinishFailed={(e) => console.log(e)}
                onFinish={onSubmit}
            >
                <Row gutter={24}>
                    {/* Left Column - Main Info */}
                    <Col xs={24} lg={16}>
                        {/* Basic Info */}
                        <Card
                            title={<span className="text-base"><InfoCircleOutlined className="mr-2 text-blue-500" />Thông tin cơ bản</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <Form.Item
                                label="Tiêu đề"
                                name="title"
                                rules={[{ required: true, message: "Vui lòng nhập tiêu đề!" }]}
                            >
                                <Input size="large" placeholder="Nhập tiêu đề bài học" onChange={handleLessonSlugChange} />
                            </Form.Item>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Slug (URL)"
                                        name="slug"
                                        rules={[{ required: true, message: "Vui lòng nhập slug!" }]}
                                        extra="Tự động tạo từ tiêu đề"
                                    >
                                        <Input size="large" placeholder="slug-cua-lesson" onBlur={handleLessonSlugChange} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Mô tả ngắn"
                                        name="description"
                                    >
                                        <Input.TextArea rows={1} placeholder="Mô tả ngắn cho bài học" showCount maxLength={200} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Content Tracks */}
                        <Card
                            title={<span className="text-base"><SoundOutlined className="mr-2 text-orange-500" />Nội dung bài học</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            bodyStyle={{ paddingTop: 0 }}
                        >
                            <Tabs items={trackTabs} size="large" />
                        </Card>

                        {/* Translations Editor */}
                        <Card
                            title={<span className="text-base"><FileTextOutlined className="mr-2 text-green-500" />Bản dịch (admin)</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <p className="text-xs text-gray-500 mb-2">Nhập bản dịch mỗi câu trên một dòng, tương ứng với nội dung gốc bên trên.</p>
                            <Form.Item label="Main Story" name="translations_mainStory">
                                <TextArea rows={6} style={{ fontFamily: 'monospace' }} onChange={handleTranslationVttChange('translations_mainStory')} />
                            </Form.Item>
                            <Form.Item label="Vocabulary" name="translations_vocabulary">
                                <TextArea rows={4} style={{ fontFamily: 'monospace' }} onChange={handleTranslationVttChange('translations_vocabulary')} />
                            </Form.Item>
                            <Form.Item label="Mini Story" name="translations_miniStory">
                                <TextArea rows={4} style={{ fontFamily: 'monospace' }} onChange={handleTranslationVttChange('translations_miniStory')} />
                            </Form.Item>
                            <Form.Item label="POV" name="translations_POV">
                                <TextArea rows={4} style={{ fontFamily: 'monospace' }} onChange={handleTranslationVttChange('translations_POV')} />
                            </Form.Item>
                            <Form.Item label="Comment" name="translations_comment">
                                <TextArea rows={4} style={{ fontFamily: 'monospace' }} onChange={handleTranslationVttChange('translations_comment')} />
                            </Form.Item>
                        </Card>
                    </Col>

                    {/* Right Column - Settings & Image */}
                    <Col xs={24} lg={8}>
                        {/* Settings */}
                        <Card
                            title={<span className="text-base"><SettingOutlined className="mr-2 text-gray-500" />Cài đặt</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <Form.Item
                                label="Trạng thái"
                                name="status"
                                rules={[{ required: true, message: "Vui lòng chọn trạng thái!" }]}
                            >
                                <Select
                                    size="large"
                                    placeholder="Chọn trạng thái"
                                    options={[
                                        { label: "🟢 Hoạt động", value: 1 },
                                        { label: "🔴 Không hoạt động", value: 2 },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Course"
                                name="course_id"
                                rules={[{ required: true, message: "Vui lòng chọn course!" }]}
                            >
                                <Select
                                    showSearch
                                    size="large"
                                    placeholder="Chọn course"
                                    optionFilterProp="children"
                                    options={courses?.map((value) => ({
                                        value: value.id,
                                        label: value.title,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Trình độ"
                                name="level"
                                extra="Ví dụ: A, B, C..."
                            >
                                <Select size="large" placeholder="Chọn trình độ" allowClear>
                                    {['A','B','C','D','E','F'].map(l => (
                                        <Select.Option key={l} value={l}>{l}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-semibold">👑 Bài học VIP</span>}
                                name="is_vip"
                                valuePropName="checked"
                                extra="Chỉ người dùng VIP mới có thể học bài này"
                            >
                                <Switch
                                    checkedChildren="VIP"
                                    unCheckedChildren="FREE"
                                />
                            </Form.Item>
                        </Card>

                        {/* Image */}
                        <Card
                            title={<span className="text-base"><PictureOutlined className="mr-2 text-green-500" />Ảnh bài học</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <Form.Item
                                name="lesson_url"
                                rules={[{ required: true, message: "Vui lòng chọn ảnh!" }]}
                            >
                                <CustomUpload type="image" accept=".png, .jpg, .jpeg, .jfif" />
                            </Form.Item>
                            <Divider dashed className="my-2">hoặc nhập URL</Divider>
                            <Form.Item name="lesson_url">
                                <Input size="large" placeholder="https://example.com/image.jpg" />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}
